"use client";

import { type CSSProperties, useEffect, useRef, useState } from "react";
import If from "./If";

export default function Image({
  src = "",
  alt = "",
  maxWidth = "",
  shouldLazyLoad = true,
  objectPosition = "center",
  cover = true,
  width = "",
  height = "",
  className = "",
  imgClassName = "",
  fillContainer = false,
  imgStyle = {},
  ...props
}) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const appendParams = (url: string, params: string) => {
    if (!params) return url;
    const hasQuery = url.includes("?");
    const needsSeparator =
      hasQuery && !url.endsWith("?") && !url.endsWith("&") ? "&" : "";
    const separator = hasQuery ? needsSeparator : "?";
    return `${url}${separator}${params}`;
  };

  useEffect(() => {
    const img = imgRef.current;

    if (!img) return;

    setLoaded(false);
    setErrored(false);

    const handleLoad = () => {
      setLoaded(true);
    };

    const handleError = () => {
      setErrored(true);
      setLoaded(true);

      if (process.env.NODE_ENV !== "production") {
        const currentSrc = img.currentSrc || img.src;
        console.error("Image failed to load", { src, currentSrc });
      }
    };

    if (img.complete) {
      if (img.naturalWidth > 0) {
        handleLoad();
      } else {
        handleError();
      }
    } else {
      img.addEventListener("load", handleLoad);
      img.addEventListener("error", handleError);
    }

    return () => {
      img.removeEventListener("load", handleLoad);
      img.removeEventListener("error", handleError);
    };
  }, [src]);

  const commonImgProps = {
    ref: imgRef,
    ...(width ? { width } : {}),
    ...(height ? { height } : {}),
    alt: alt,
    loading: (shouldLazyLoad ? "lazy" : "eager") as
      | "lazy"
      | "eager"
      | undefined,
    decoding: "async" as const,
    fetchPriority: (shouldLazyLoad ? "low" : "high") as
      | "high"
      | "low"
      | "auto"
      | undefined,
    className: `block ${objectPosition === "center" ? "object-center" : ""}
      ${cover ? "object-cover" : "object-contain"}
      ${imgClassName}
      ${fillContainer ? "h-full w-full" : ""}
      transition-opacity duration-300 ease-in
      ${loaded || errored ? "opacity-100" : "opacity-0"}`,
    style: {
      ...imgStyle,
      display: "block",
    } as CSSProperties,
  };

  const renderImage = (srcWithParams: string) => (
    <img {...commonImgProps} src={srcWithParams} decoding="async" />
  );

  return (
    <picture
      className={`block ${className} ${fillContainer ? "h-full w-full" : ""}`}
      {...props}
    >
      <If condition={!src?.includes("bfldr.com")}>
        <source
          type="image/avif"
          media="(max-width: 500px)"
          srcSet={appendParams(src, "fm=avif&w=1000")}
        />
        <source
          type="image/avif"
          media="(max-width: 1200px)"
          srcSet={appendParams(src, "fm=avif&w=1200")}
        />
        <source
          type="image/avif"
          media="(min-width: 1201px)"
          srcSet={appendParams(
            src,
            `fm=avif${maxWidth ? `&w=${maxWidth}` : ""}`,
          )}
        />
        <source
          type="image/webp"
          media="(max-width: 500px)"
          srcSet={appendParams(src, "fm=webp&w=1000")}
        />
        <source
          type="image/webp"
          media="(max-width: 1200px)"
          srcSet={appendParams(src, "fm=webp&w=1200")}
        />
        <source
          type="image/webp"
          media="(min-width: 1201px)"
          srcSet={appendParams(
            src,
            `fm=webp${maxWidth ? `&w=${maxWidth}` : ""}`,
          )}
        />
        <source
          type="image/jpeg"
          media="(max-width: 500px)"
          srcSet={appendParams(src, "fm=jpg&w=1000")}
        />
        <source
          type="image/jpeg"
          media="(max-width: 1200px)"
          srcSet={appendParams(src, "fm=jpg&w=1200")}
        />
        <source
          type="image/jpeg"
          media="(min-width: 1201px)"
          srcSet={appendParams(
            src,
            `fm=jpg${maxWidth ? `&w=${maxWidth}` : ""}`,
          )}
        />
        {renderImage(appendParams(src, maxWidth ? `w=${maxWidth}` : ""))}
      </If>

      <If condition={src?.includes("bfldr.com")}>
        <source
          type="image/webp"
          media="(max-width: 500px)"
          srcSet={appendParams(src, "w=1000")}
        />
        <source
          type="image/webp"
          media="(max-width: 1200px)"
          srcSet={appendParams(src, "w=1200")}
        />
        <source
          type="image/webp"
          media="(min-width: 1201px)"
          srcSet={appendParams(src, maxWidth ? `w=${maxWidth}` : "")}
        />
        <source
          type="image/jpeg"
          media="(max-width: 500px)"
          srcSet={appendParams(src, "w=1000")}
        />
        <source
          type="image/jpeg"
          media="(max-width: 1200px)"
          srcSet={appendParams(src, "w=1200")}
        />
        <source
          type="image/jpeg"
          media="(min-width: 1201px)"
          srcSet={appendParams(src, maxWidth ? `w=${maxWidth}` : "")}
        />
        {renderImage(appendParams(src, maxWidth ? `w=${maxWidth}` : ""))}
      </If>
    </picture>
  );
}
